/**
 * OTP Handler Component
 * Handles OTP verification with mock data support for testing
 * 
 * Location: ${TOP-SRC}/src/components/common/OTPHandler/otpHandler.js
 */

import React, { useState } from "react";
import Modal from "react-modal";
import { requestOTP, validateOTP, resendOTP } from "../../api/auth/otpService";
import { USE_MOCK_DATA } from "../../config/apiConfig";

// Set app element for accessibility
if (typeof document !== 'undefined') {
  Modal.setAppElement(document.getElementById('root') || document.body);
}

/**
 * OTP Handler Component
 * Can be used as standalone or embedded in other forms
 */
export const OTPHandler = ({ 
  onOTPVerified, 
  initialMobile = "", 
  autoOpenModal = false 
}) => {
  const [devices, setDevices] = useState([]);
  const [mobileNumber, setMobileNumber] = useState(initialMobile);
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(autoOpenModal);
  const [isOtpValid, setIsOtpValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle OTP Request
   */
  const handleRequestOTP = async () => {
    setErrorMessage("");
    setIsLoading(true);

    const result = await requestOTP(mobileNumber);

    if (result.success) {
      setIsOtpSent(true);
      setShowOTPModal(true);
      
      // Show OTP in console for testing (mock mode only)
      if (USE_MOCK_DATA && result.otp) {
        console.log('🎭 MOCK OTP:', result.otp);
        alert(`🎭 MOCK MODE: Your OTP is ${result.otp}`);
      }
    } else {
      setErrorMessage(result.message);
    }

    setIsLoading(false);
  };

  /**
   * Handle OTP Validation
   */
  const handleValidateOTP = async () => {
    setErrorMessage("");
    setIsLoading(true);

    const result = await validateOTP(mobileNumber, otp);

    if (result.success) {
      setIsOtpValid(true);
      setShowOTPModal(false);
      
      // Store device ID
      if (result.deviceId) {
        setDevices((prevDevices) => [...prevDevices, result.deviceId]);
      }

      // Callback for parent component
      if (onOTPVerified) {
        onOTPVerified({
          mobileNumber,
          deviceId: result.deviceId,
          verified: true,
        });
      }

      alert("✅ OTP Verified Successfully!");
    } else {
      setIsOtpValid(false);
      setErrorMessage(result.message);
    }

    setIsLoading(false);
  };

  /**
   * Handle Resend OTP
   */
  const handleResendOTP = async () => {
    setErrorMessage("");
    setOtp("");
    await handleRequestOTP();
  };

  return (
    <div style={styles.container}>
      {USE_MOCK_DATA && (
        <div style={styles.mockBanner}>
          🎭 MOCK MODE ENABLED - Any 6-digit OTP will work
        </div>
      )}

      {/* Mobile Number Input */}
      <div style={styles.inputContainer}>
        <input
          type="text"
          style={styles.input}
          placeholder="Enter 10-digit Mobile Number"
          value={mobileNumber}
          onChange={(e) => setMobileNumber(e.target.value)}
          maxLength={10}
          disabled={isOtpSent}
        />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div style={styles.errorMessage}>
          ⚠️ {errorMessage}
        </div>
      )}

      {/* Success Message */}
      {isOtpValid && (
        <div style={styles.successMessage}>
          ✅ Mobile number verified successfully!
        </div>
      )}

      {/* OTP Request Button */}
      <div style={styles.buttonContainer}>
        <button 
          onClick={handleRequestOTP} 
          disabled={isOtpSent || isLoading || mobileNumber.length !== 10}
          style={{
            ...styles.button,
            opacity: (isOtpSent || isLoading || mobileNumber.length !== 10) ? 0.6 : 1,
            cursor: (isOtpSent || isLoading || mobileNumber.length !== 10) ? 'not-allowed' : 'pointer',
            background: "black",
            color: "white"
          }}
        >
          {isLoading ? "Sending..." : isOtpSent ? "OTP Sent ✓" : "Get OTP"}
        </button>
      </div>

      {/* OTP Modal */}
      <Modal 
        isOpen={showOTPModal} 
        onRequestClose={() => setShowOTPModal(false)}
        style={modalStyles}
      >
        <div style={styles.modalContent}>
          <h2 style={styles.modalTitle}>Enter OTP</h2>
          <p style={styles.modalSubtitle}>
            We've sent a 6-digit OTP to <strong>{mobileNumber}</strong>
          </p>

          {USE_MOCK_DATA && (
            <div style={styles.mockInfo}>
              🎭 Mock Mode: Enter any 6-digit number
            </div>
          )}

          <input
            type="text"
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            maxLength={6}
            style={styles.otpInput}
            autoFocus
          />

          {errorMessage && (
            <div style={styles.errorMessage}>
              {errorMessage}
            </div>
          )}

          <div style={styles.modalButtons}>
            <button
              onClick={handleValidateOTP}
              disabled={isLoading || otp.length !== 6}
              style={{
                ...styles.submitButton,
                opacity: (isLoading || otp.length !== 6) ? 0.6 : 1,
              }}
            >
              {isLoading ? "Verifying..." : "Verify OTP"}
            </button>
            
            <button
              onClick={handleResendOTP}
              disabled={isLoading}
              style={styles.resendButton}
            >
              Resend OTP
            </button>

            <button 
              onClick={() => setShowOTPModal(false)} 
              style={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ========================================
// STYLES
// ========================================

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  title: {
    marginLeft: '10px',
    marginBottom: '15px',
    fontSize: '18px',
    fontWeight: '600',
  },
  mockBanner: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
    textAlign: 'center',
    fontWeight: '600',
  },
  inputContainer: {
    marginBottom: '10px',
  },
  input: {
    width: '100%',
    maxWidth: '300px',
    padding: '10px',
    marginLeft: '10px',
    background: 'white',
    color: 'black',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '16px',
  },
  buttonContainer: {
    marginTop: '15px',
  },
  button: {
    width: '150px',
    marginLeft: '10px',
    height: '40px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
  },
  errorMessage: {
    color: '#dc3545',
    marginLeft: '10px',
    marginTop: '10px',
    fontSize: '14px',
  },
  successMessage: {
    color: '#28a745',
    marginLeft: '10px',
    marginTop: '10px',
    fontSize: '14px',
    fontWeight: '600',
  },
  modalContent: {
    padding: '20px',
  },
  modalTitle: {
    fontSize: '24px',
    marginBottom: '10px',
  },
  modalSubtitle: {
    fontSize: '16px',
    marginBottom: '20px',
    color: '#666',
  },
  mockInfo: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
    textAlign: 'center',
  },
  otpInput: {
    width: '100%',
    padding: '12px',
    fontSize: '20px',
    textAlign: 'center',
    letterSpacing: '8px',
    background: 'white',
    color: 'black',
    border: '2px solid #ddd',
    borderRadius: '5px',
    marginBottom: '15px',
  },
  modalButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  submitButton: {
    flex: 1,
    minWidth: '120px',
    padding: '12px',
    background: 'rgba(36,36,45,0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
  },
  resendButton: {
    flex: 1,
    minWidth: '120px',
    padding: '12px',
    background: '#17a2b8',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  cancelButton: {
    flex: 1,
    minWidth: '120px',
    padding: '12px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
  },
};

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '500px',
    width: '90%',
    borderRadius: '10px',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
  },
};

export default OTPHandler;

// Export individual functions for use in other components
export { requestOTP, validateOTP, resendOTP };
